import { PARK_STATUS_OPTIONS } from "../model/materielConstants";
import { dueSoonCount, parkMetrics, parkStatusColors, parkStatusLabel, rentalAlertText, rentalAlertTone } from "../model/parkRules";
import { buttonPrimary, buttonSecondary, fieldStyle, panelStyle, parkCellStyle } from "./parkStyles";

// Bandeau du parc : titre, échéances proches, bouton d'ajout et indicateurs par statut.
export function ParkHeader({ items, onAdd }) {
    const metrics = parkMetrics(items);
    const dueSoon = dueSoonCount(items);
    return (
        <>
            <div style={{ ...panelStyle, background: "#0C3B3C", color: "white", padding: "18px", display: "grid", gridTemplateColumns: "1fr auto", gap: "16px", alignItems: "center" }}>
                <div>
                    <div style={{ fontFamily: "Fraunces, Georgia, serif", fontSize: "24px", fontWeight: 800 }}>Parc matériel</div>
                    <div style={{ marginTop: "4px", color: "#DDEBE4", fontSize: "13px" }}>
                        Suivi du stock, des locations, des procédures et des révisions.
                    </div>
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    {dueSoon > 0 && (
                        <span style={{ background: "#FFF7E0", color: "#7A5A00", borderRadius: "999px", padding: "8px 11px", fontSize: "12px", fontWeight: 900 }}>
                            {dueSoon} échéance(s)
                        </span>
                    )}
                    <button type="button" onClick={onAdd} style={{ ...buttonPrimary, background: "#17B978", color: "#0C3B3C" }}>Ajouter un matériel</button>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(130px, 1fr))", gap: "10px" }}>
                {metrics.map(metric => (
                    <div key={metric.label} style={{ ...panelStyle, padding: "13px 14px" }}>
                        <div style={{ color: "#5A6D68", fontSize: "12px", fontWeight: 800 }}>{metric.label}</div>
                        <div style={{ color: "#0C3B3C", fontSize: "25px", fontWeight: 900, marginTop: "3px" }}>{metric.value}</div>
                    </div>
                ))}
            </div>
        </>
    );
}

export function ParkFilters({ park }) {
    const { isParkCompact, query, setQuery, statusFilter, setStatusFilter, refresh } = park;
    return (
        <div style={{ ...panelStyle, padding: "13px", display: "grid", gridTemplateColumns: isParkCompact ? "minmax(0, 1fr)" : "minmax(220px, 1fr) 190px auto", gap: "10px", alignItems: "center" }}>
            <input style={fieldStyle} placeholder="Rechercher numéro, matricule, locataire, procédure..." value={query} onChange={e => setQuery(e.target.value)} />
            <select style={fieldStyle} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">Tous les statuts</option>
                {PARK_STATUS_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <button type="button" onClick={refresh} style={buttonSecondary}>Actualiser</button>
        </div>
    );
}

function ParkTableRow({ item, selected, onSelect, onEdit }) {
    const colors = parkStatusColors(item.status);
    return (
        <tr onClick={() => onSelect(item)} style={{ background: selected ? "#F4F1EA" : "white", cursor: "pointer" }}>
            <td style={parkCellStyle}>
                <div style={{ color: "#122A26", fontWeight: 900 }}>{item.internalNumber}</div>
                <div style={{ color: "#5A6D68", fontSize: "12px", marginTop: "2px" }}>{item.matriculeName || "Matricule non renseigné"}</div>
            </td>
            <td style={parkCellStyle}>
                <span style={{ display: "inline-flex", border: `1px solid ${colors.border}`, background: colors.bg, color: colors.color, borderRadius: "999px", padding: "5px 9px", fontSize: "12px", fontWeight: 900 }}>
                    {parkStatusLabel(item.status)}
                </span>
            </td>
            <td style={{ ...parkCellStyle, color: "#122A26" }}>{item.currentTenant || "—"}</td>
            <td style={{ ...parkCellStyle, color: rentalAlertTone(item), fontWeight: 900 }}>{rentalAlertText(item)}</td>
            <td style={{ ...parkCellStyle, color: "#5A6D68" }}>{item.maintenanceProcedureName || "—"}</td>
            <td style={{ ...parkCellStyle, color: "#5A6D68" }}>{item.lastRevisionDate || "—"}</td>
            <td style={{ padding: "9px 12px", borderBottom: "1px solid #F1EEE6", whiteSpace: "nowrap" }}>
                <button type="button" onClick={event => { event.stopPropagation(); onEdit(item); }} style={{ ...buttonSecondary, padding: "7px 10px" }}>Modifier</button>
            </td>
        </tr>
    );
}

function ParkTableContent({ park, onEdit }) {
    const { loading, filteredItems, selectedItem, setSelectedId, setPanelMode } = park;
    if (loading) return <div style={{ padding: "22px", color: "#5A6D68", fontSize: "13px" }}>Chargement du parc...</div>;
    if (filteredItems.length === 0) return <div style={{ padding: "22px", color: "#5A6D68", fontSize: "13px" }}>Aucun matériel ne correspond aux filtres.</div>;
    return (
        <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", background: "white" }}>
                <thead>
                    <tr style={{ background: "#F4F1EA", color: "#5A6D68", textAlign: "left" }}>
                        {["Matériel", "Statut", "Locataire", "Échéance", "Entretien", "Révision", ""].map(label => (
                            <th key={label} style={{ padding: "11px 12px", borderBottom: "1px solid #E1DCCF", whiteSpace: "nowrap", fontWeight: 900 }}>{label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filteredItems.map(item => (
                        <ParkTableRow
                            key={item.id}
                            item={item}
                            selected={selectedItem?.id === item.id}
                            onSelect={selected => { setSelectedId(selected.id); setPanelMode("detail"); }}
                            onEdit={onEdit}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Liste du parc filtré ; cliquer une ligne affiche sa fiche, « Modifier » ouvre son formulaire.
export function ParkTable({ park, onEdit }) {
    return (
        <div style={{ ...panelStyle, overflow: "hidden" }}>
            <ParkTableContent park={park} onEdit={onEdit} />
        </div>
    );
}
